import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGoalStore, getWeekStartMs } from "../../../hooks/useGoalStore";
import { useGrowStore } from "../../../hooks/useGrowStore";

const DAYS = ["월", "화", "수", "목", "금", "토", "일"];

function GoalEditModal({ current, onSave, onClose }: { current: number; onSave: (v: number) => void; onClose: () => void }) {
  const [val, setVal] = useState(current);
  const descriptions = ["", "주 1회 — 가볍게 시작", "주 2회 — 꾸준한 학습", "주 3회 — 표준 페이스", "주 4회 — 적극적 학습", "주 5회 — 집중 모드", "주 6회 — 거의 매일", "주 7회 — 매일 도전"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-3xl w-full max-w-sm p-7">
        <h3 className="text-lg font-black text-[#191F28] mb-1">이번 주 목표 설정</h3>
        <p className="text-xs text-[#8B95A1] mb-6">일주일에 몇 개 클래스를 완료할까요?</p>

        {/* Number Picker */}
        <div className="flex items-center justify-center gap-4 mb-4">
          <button
            onClick={() => setVal((v) => Math.max(1, v - 1))}
            className="w-12 h-12 rounded-full bg-[#F2F4F6] flex items-center justify-center text-xl cursor-pointer hover:bg-[#E8E9EB] transition-colors"
          >-</button>
          <div className="w-20 h-20 rounded-2xl bg-[#00D873]/10 flex flex-col items-center justify-center">
            <span className="text-3xl font-black text-[#00D873]">{val}</span>
            <span className="text-xs text-[#8B95A1]">개</span>
          </div>
          <button
            onClick={() => setVal((v) => Math.min(7, v + 1))}
            className="w-12 h-12 rounded-full bg-[#F2F4F6] flex items-center justify-center text-xl cursor-pointer hover:bg-[#E8E9EB] transition-colors"
          >+</button>
        </div>

        <p className="text-center text-sm font-semibold text-[#00D873] mb-6">{descriptions[val]}</p>

        {/* Dots Preview */}
        <div className="flex gap-2 justify-center mb-6">
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i} className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold transition-all ${i < val ? "bg-[#00D873] text-white" : "bg-[#F2F4F6] text-[#C8CDD4]"}`}>
              {DAYS[i]}
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-2xl bg-[#F2F4F6] text-[#191F28] font-bold text-sm cursor-pointer whitespace-nowrap">취소</button>
          <button
            onClick={() => { onSave(val); onClose(); }}
            className="flex-1 py-3 rounded-2xl bg-[#00D873] text-white font-bold text-sm cursor-pointer whitespace-nowrap"
          >저장하기</button>
        </div>
      </div>
    </div>
  );
}

export default function WeeklyGoalSection() {
  const { goal, setTarget } = useGoalStore();
  const { store } = useGrowStore();
  const [showEdit, setShowEdit] = useState(false);
  const navigate = useNavigate();

  const weekStart = getWeekStartMs();

  const completedThisWeek = useMemo(
    () => store.completedClasses.filter((c) => (c.completedAtMs ?? 0) >= weekStart),
    [store.completedClasses, weekStart]
  );

  const count = completedThisWeek.length;
  const pct = Math.min(100, Math.round((count / goal.target) * 100));
  const isDone = count >= goal.target;

  // Build daily progress (which days this week had completions)
  const dayCompletions = useMemo(() => {
    const arr = new Array(7).fill(false);
    completedThisWeek.forEach((c) => {
      if (!c.completedAtMs) return;
      const d = new Date(c.completedAtMs);
      const dayIdx = (d.getDay() + 6) % 7; // Mon=0, Tue=1...
      arr[dayIdx] = true;
    });
    return arr;
  }, [completedThisWeek]);

  const today = (new Date().getDay() + 6) % 7; // Mon=0

  return (
    <div className="bg-white rounded-3xl p-7">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-black text-[#191F28]">이번 주 학습 목표</h3>
          <p className="text-xs text-[#8B95A1] mt-0.5">월요일부터 일요일까지 트래킹</p>
        </div>
        <button
          onClick={() => setShowEdit(true)}
          className="flex items-center gap-1.5 text-xs font-bold text-[#8B95A1] hover:text-[#191F28] transition-colors cursor-pointer bg-[#F2F4F6] px-3 py-2 rounded-full whitespace-nowrap"
        >
          <i className="ri-settings-3-line" />목표 변경
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* Left: Progress */}
        <div className="flex-1">
          {/* Big progress display */}
          <div className={`rounded-2xl p-6 mb-4 transition-all ${isDone ? "bg-gradient-to-r from-[#E8F5E9] to-[#F0FFF7] border border-[#00D873]/30" : "bg-[#F9F9F9]"}`}>
            <div className="flex items-end gap-3 mb-4">
              <span className="text-5xl font-black text-[#191F28]">{count}</span>
              <span className="text-xl font-bold text-[#8B95A1] mb-1">/ {goal.target}개</span>
              {isDone && <span className="ml-auto text-2xl">🎉</span>}
            </div>

            {/* Progress bar */}
            <div className="h-3 bg-white rounded-full overflow-hidden mb-2">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, backgroundColor: isDone ? "#00D873" : "#00D873" }}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#8B95A1]">{pct}% 달성</span>
              {isDone ? (
                <span className="text-xs font-extrabold text-[#00D873]">🏆 이번 주 목표 달성!</span>
              ) : (
                <span className="text-xs text-[#8B95A1]">{goal.target - count}개 남았어요</span>
              )}
            </div>
          </div>

          {/* Daily checkin dots */}
          <div className="flex gap-2">
            {DAYS.map((d, i) => {
              const done = dayCompletions[i];
              const isToday = i === today;
              return (
                <div key={d} className="flex-1 flex flex-col items-center gap-1.5">
                  <div
                    className={`w-full aspect-square rounded-xl flex items-center justify-center text-xs font-bold transition-all ${
                      done ? "bg-[#00D873] text-white" : isToday ? "bg-[#00D873]/10 border-2 border-[#00D873]/40 text-[#00D873]" : "bg-[#F2F4F6] text-[#C8CDD4]"
                    }`}
                  >
                    {done ? <i className="ri-check-line text-sm" /> : <span className="text-[10px]">{d}</span>}
                  </div>
                  <span className="text-[9px] text-[#C8CDD4]">{d}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Recent completed this week */}
        <div className="lg:w-56 w-full shrink-0">
          <p className="text-xs font-extrabold text-[#191F28] mb-3">이번 주 완료한 클래스</p>
          {completedThisWeek.length === 0 ? (
            <div className="bg-[#F9F9F9] rounded-2xl p-4 text-center">
              <i className="ri-book-open-line text-2xl text-[#C8CDD4] mb-2" />
              <p className="text-xs text-[#8B95A1]">아직 없어요</p>
              <button
                onClick={() => navigate("/explore")}
                className="mt-3 text-xs font-bold text-[#00D873] cursor-pointer whitespace-nowrap"
              >
                클래스 찾기 →
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {completedThisWeek.slice(0, 3).map((cls) => (
                <div key={cls.classId} className="bg-[#F9F9F9] rounded-xl p-3 flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-[#00D873]/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-[#00D873] text-sm" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-[#191F28] truncate">{cls.classTitle}</p>
                    <p className="text-[10px] text-[#8B95A1]">{cls.avgScore}점</p>
                  </div>
                </div>
              ))}
              {completedThisWeek.length > 3 && (
                <p className="text-xs text-[#8B95A1] text-center">+{completedThisWeek.length - 3}개 더</p>
              )}
            </div>
          )}
        </div>
      </div>

      {showEdit && (
        <GoalEditModal
          current={goal.target}
          onSave={setTarget}
          onClose={() => setShowEdit(false)}
        />
      )}
    </div>
  );
}
